import { TenantRole, TenantUserSettings } from '@/api/generatedApi';
import { useGetCurrentUser } from '@/hooks/api/users/useGetCurrentUser';
import { useAuthService } from '@/services/auth';
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTenants } from './useTenants';
import { getTenantShortNameFromUrl } from './utils';

const getEnabledTenants = (settings: TenantUserSettings[]) => settings.filter(s => s.userEnabled);

const selectFallbackTenant = (enabledTenants: TenantUserSettings[]) => {
  const nonSearchTenant = enabledTenants.find(s => s.tenantRole !== TenantRole.Search);
  return nonSearchTenant || enabledTenants[0];
};

const buildTenantPath = (tenant: TenantUserSettings) => `/tenants/${tenant.tenantShortName}/assets`;

export const TenantUrlSync = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuthService();
  const { data: user } = useGetCurrentUser({ skipRedirect: true, retry: false });
  const { tenant, setTenant } = useTenants();

  useEffect(() => {
    if (user?.user?.settings) {
      const enabledTenants = getEnabledTenants(user.user.settings);
      if (enabledTenants.length === 0) {
        logout();
        return;
      }
    }

    const urlTenantShortName = getTenantShortNameFromUrl(location.pathname);
    const urlTenant = user?.user?.settings?.find(
      tenant => tenant.tenantShortName === urlTenantShortName && tenant.userEnabled
    );

    if (urlTenant) {
      const tenantIdDoesNotMatch = urlTenant.tenantId !== tenant?.tenantId;
      const tenantShortNameDoesNotMatch = urlTenant.tenantShortName !== tenant?.tenantShortName;

      if (tenantIdDoesNotMatch || tenantShortNameDoesNotMatch) {
        setTenant(urlTenant);
      }
    } else if (urlTenantShortName && user?.user?.settings) {
      const enabledTenants = getEnabledTenants(user.user.settings);
      const fallbackTenant = selectFallbackTenant(enabledTenants);

      if (fallbackTenant) {
        const redirectPath = buildTenantPath(fallbackTenant);
        setTenant(fallbackTenant);
        navigate(redirectPath, { replace: true });
      }
    }
  }, [location.pathname, user?.user?.settings, tenant, setTenant, navigate, logout]);

  return <></>;
};
